import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { Link2, AlertTriangle, ExternalLink, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	getCanonicalIssueSubtype,
	type PageIssue,
	type CanonicalDetails,
	type CanonicalFix,
} from "@/types/technicalAudit";

interface CanonicalCardProps {
	issue: PageIssue;
}

// The canonical resolver/apply suggestion is stored as JSON in
// proposed_value / approved_value (mirrors indexability + the other resolvers).
// Prefer the user-edited approved_value, then proposed_value, then the structured
// proposed_fix object. Returns the merged CanonicalFix the card renders from.
function parseCanonicalSuggestion(issue: PageIssue): CanonicalFix | null {
	const raw = (issue.approved_value as unknown) ?? (issue.proposed_value as unknown);
	let parsed: CanonicalFix | null = null;
	if (raw && typeof raw === "object") {
		parsed = raw as CanonicalFix;
	} else if (typeof raw === "string" && raw.trim()) {
		try {
			parsed = JSON.parse(raw) as CanonicalFix;
		} catch {
			parsed = null;
		}
	}
	const structured = issue.proposed_fix as CanonicalFix | null;
	if (parsed && structured) return { ...structured, ...parsed };
	return parsed ?? structured ?? null;
}

const issueTypeBadge: Record<string, { label: string; className: string }> = {
	missing: {
		label: "Missing",
		className: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
	},
	multiple: {
		label: "Multiple",
		className: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
	},
	mismatch: {
		label: "Mismatch",
		className: "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800",
	},
};

export function CanonicalCard({ issue }: CanonicalCardProps) {
	const rawDetails = (issue.issue_details || {}) as CanonicalDetails;
	const canonicalUrls = Array.isArray(rawDetails.canonical_urls)
		? rawDetails.canonical_urls
		: [];
	const details = {
		...rawDetails,
		page_url: rawDetails.page_url || issue.page_url,
		issue_type: getCanonicalIssueSubtype(rawDetails),
		canonical_url:
			rawDetails.canonical_url ||
			rawDetails.canonical_1 ||
			canonicalUrls[0],
	};
	const secondCanonical = rawDetails.canonical_2 || canonicalUrls[1];
	const proposedFix = parseCanonicalSuggestion(issue);
	const recommendedCanonical =
		proposedFix?.recommended_canonical ||
		proposedFix?.canonical_url ||
		rawDetails.recommended_canonical;
	const canonicalCause = proposedFix?.canonical_cause || rawDetails.canonical_cause;
	// Cross-domain canonical is the review-only gate: no automatic write; the
	// user can force it via "Apply anyway". Other causes apply directly.
	const isReviewOnly =
		proposedFix?.review_only === true || proposedFix?.action === "review";
	const isCrossDomain = canonicalCause === "cross_domain";
	const badge = issueTypeBadge[details.issue_type] || issueTypeBadge.missing;

	return (
		<div className="space-y-4">
			{/* Issue Type Banner */}
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">
						Canonical Tag Issue
					</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						{details.issue_type === "missing" &&
							"No canonical tag found on this page. Search engines may index duplicate versions."}
						{details.issue_type === "multiple" &&
							"Multiple canonical tags detected. Only one canonical URL should be specified per page."}
						{details.issue_type === "mismatch" &&
							"The canonical URL does not match the page URL, which may cause indexing issues."}
					</p>
				</div>
				<Badge variant="outline" className={badge.className}>
					{badge.label}
				</Badge>
			</div>

			{/* Page URL Card */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Page URL
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={details.page_url}
						onClick={(e) => openWithCacheBuster(e, details.page_url)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{details.page_url}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>

			{/* Canonical URL Card */}
			{details.canonical_url && (
				<Card className="border-border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
							<div className={cn(
								"w-8 h-8 rounded-lg flex items-center justify-center",
								details.issue_type === "mismatch" ? "bg-orange-50 dark:bg-orange-950/40" : "bg-muted",
							)}>
								<Link2 className={cn(
									"w-4 h-4",
									details.issue_type === "mismatch" ? "text-orange-600 dark:text-orange-400" : "text-muted-foreground",
								)} />
							</div>
							Current Canonical
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<a
							href={details.canonical_url}
							onClick={(e) => openWithCacheBuster(e, details.canonical_url)}
							target="_blank"
							rel="noopener noreferrer"
							className={cn(
								"group flex items-start gap-2 p-3 rounded-lg border transition-all hover:shadow-sm",
								details.issue_type === "mismatch"
									? "bg-orange-50 dark:bg-orange-950/40/50 border-orange-200 dark:border-orange-800 hover:border-orange-300"
									: "bg-muted border-border hover:border-border",
							)}
						>
							<p className={cn(
								"text-sm font-mono break-all flex-1",
								details.issue_type === "mismatch" ? "text-orange-700 dark:text-orange-400" : "text-foreground",
							)}>
								{details.canonical_url}
							</p>
							<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
						</a>
					</CardContent>
				</Card>
			)}

			{secondCanonical && (
				<Card className="border-amber-200 dark:border-amber-800 shadow-sm bg-amber-50 dark:bg-amber-950/40/30">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
								<Link2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
							</div>
							Additional Canonical
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<a
							href={secondCanonical}
							onClick={(e) => openWithCacheBuster(e, secondCanonical)}
							target="_blank"
							rel="noopener noreferrer"
							className="group flex items-start gap-2 p-3 rounded-lg border bg-card border-amber-200 dark:border-amber-800 hover:border-amber-300 transition-all"
						>
							<p className="text-sm font-mono break-all text-amber-800 dark:text-amber-300 flex-1">
								{secondCanonical}
							</p>
							<ExternalLink className="w-4 h-4 text-amber-500 dark:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
						</a>
					</CardContent>
				</Card>
			)}

			{/* Review-only / cross-domain gate — mirrors IndexabilityCard's
			    review-only messaging. The user can still "Apply anyway" (force). */}
			{isReviewOnly && (
				<Card className="border-amber-200 dark:border-amber-800 shadow-sm bg-amber-50 dark:bg-amber-950/40/40">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
								<Eye className="w-4 h-4 text-amber-700 dark:text-amber-400" />
							</div>
							Review only — not auto-applied
							{canonicalCause && (
								<Badge
									variant="outline"
									className="ml-auto bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
								>
									{canonicalCause}
								</Badge>
							)}
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<div className="p-3 bg-card rounded-lg border border-amber-200 dark:border-amber-800">
							<p className="text-sm text-amber-900 dark:text-amber-300">
								{isCrossDomain
									? "This page canonicalises to a different domain. Cross-domain canonicals are not changed automatically — confirm the target is correct, then use “Apply anyway” to override and set a self-canonical instead."
									: proposedFix?.reasoning ||
										"This canonical needs manual review; no automatic fix is applied."}
							</p>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Proposed Fix */}
			{recommendedCanonical && (
				<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
								<Link2 className="w-4 h-4 text-green-600 dark:text-green-400" />
							</div>
							Recommended Canonical
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						{/* Say what applying does — the URL alone left users guessing (GH #599). */}
						<p className="text-xs text-muted-foreground">
							{isReviewOnly
								? "Review only — confirm this is the right canonical, then set it by hand in WordPress."
								: 'Apply sets the page’s canonical tag (link rel="canonical") to this URL.'}
						</p>
						<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
							<p className="text-sm font-mono break-all text-green-800 dark:text-green-300">
								{recommendedCanonical}
							</p>
						</div>
						{!isReviewOnly && proposedFix?.reasoning && (
							<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
								<p className="text-sm text-green-800 dark:text-green-300">{proposedFix.reasoning}</p>
							</div>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
